import React, { useEffect, useRef, useState } from "react";
import ImageUploadBox from "../ImageUploadBox/index.jsx";
import { ReactComponent as TrashSvg } from "../../../assets/admin/icons/trash.svg";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ReactComponent as FileUploaderPlaceholderSvg } from "../../../assets/icons/fileuploader-placeholder.svg";
import { Cropper } from "react-cropper";
import { toast } from "react-toastify";
import _ from "lodash";
import "cropperjs/dist/cropper.css";
import "../../../styles/imageCropper.scss";
import "swiper/scss";

function useDebounceEffect(fn, waitTime, deps) {
  useEffect(() => {
    const t = setTimeout(() => {
      fn.apply(undefined, deps);
    }, waitTime);

    return () => {
      clearTimeout(t);
    };
  }, deps);
}

const ImageCropperEx = ({
  name,
  onChange: fireEventChange,
  aspectRatio,
  defaultImages,
  multiple,
  withGallery,
  cropperHeight,
  circular,
  limit = 1,
}) => {
  const [aspect] = useState(aspectRatio ? aspectRatio : undefined);
  const [crop, setCrop] = useState(undefined);
  const [images, setImages] = useState([]);
  const cropRef = useRef(null);
  const imageRef = useRef(null);
  const imageCropperRef = useRef(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(undefined);
  const [isCropperReady, setIsCropperReady] = useState(false);

  const style = {
    width: "100%",
    maxHeight: cropperHeight ?? "auto",
  };

  useDebounceEffect(
    async () => {
      if (!crop) return;
      if (selectedImageIndex === undefined) return;

      const cropper = cropRef.current?.cropper;
      if (!cropper) {
        return;
      }

      cropper.getCroppedCanvas().toBlob((blob) => {
        if (!blob) return;

        const data = cropper.getData();

        // * Update images data
        setImages((curr) =>
          curr.map((image, index) => {
            if (index !== selectedImageIndex) return image;

            image.blob = blob;
            image.croppedDataUrl = cropper
              .getCroppedCanvas()
              .toDataURL("image/jpeg");
            image.crop = {
              x: data.x,
              y: data.y,
              width: data.width,
              height: data.height,
            };

            return image;
          })
        );
      }, "image/jpeg");
    },
    100,
    [crop]
  );

  useEffect(() => {
    fireEventChange(images);
  }, [images]);

  useEffect(() => {
    if (selectedImageIndex === undefined) return;
    setIsCropperReady(false);
    cropRef.current.cropper.replace(images[selectedImageIndex].dataUrl);
  }, [selectedImageIndex]);

  useEffect(() => {
    if (selectedImageIndex === undefined) return;
    if (!isCropperReady) return;

    setIsCropperReady(false);

    const _crop = images[selectedImageIndex].crop;

    if (!_crop) return;

    const { top, left, width, naturalWidth } =
      cropRef.current.cropper.getCanvasData();

    const _ratio = width / naturalWidth;

    cropRef.current.cropper.setCropBoxData({
      top: _crop.y * _ratio + top,
      left: _crop.x * _ratio + left,
      width: _crop.width * _ratio,
    });
  }, [selectedImageIndex, isCropperReady, images]);

  useEffect(() => {
    if (!defaultImages || !defaultImages?.length) return;
    if (images.length > 0) return;

    setImages(defaultImages);
    setSelectedImageIndex(0);
  }, [defaultImages]);

  const handleFileChange = (files) => {
    if (files.length === 0) return;

    if (images.length + files.length > limit) {
      toast.error(`Only allowed total ${limit} images`);
      return;
    }

    // * Makes crop preview update between images.
    setCrop(undefined);

    Object.keys(files).forEach((i) => {
      // * Check if file already exists
      const file = files[i];

      if (images.find((image) => image.file.name === file.name)) {
        return;
      }

      if (!multiple && i > 0) return;

      const reader = new FileReader();

      reader.onload = (e) => {
        // * Add to list of images
        setImages((curr) => [
          ...curr,
          {
            file: file,
            dataUrl: reader.result?.toString(),
          },
        ]);

        if (i > 0) return;

        setSelectedImageIndex(0);
      };

      reader.readAsDataURL(file);
    });

    // setIsShowUploadBox(false);
    // setIsDeleteCrop(false);
  };

  const onCropEnd = () => {
    const cropper = cropRef.current?.cropper;
    if (!cropper) {
      return;
    }

    const data = cropper.getData();

    setCrop({
      x: data.x,
      y: data.y,
      width: data.width,
      height: data.height,
    });
  };

  const handleGalleryImageDragEnd = (result) => {
    const originalIndex = result.source.index;
    const newIndex = result.destination.index;

    // * Swap panorama images
    const _images = _.cloneDeep(images);
    const _draggedImage = _images.splice(originalIndex, 1);
    _images.splice(newIndex, 0, ..._draggedImage);
    setImages(_images);

    if (originalIndex === selectedImageIndex) {
      setSelectedImageIndex(newIndex);
    }

    // * image is replaced with other image must move index
    if (newIndex === selectedImageIndex && originalIndex < newIndex) {
      setSelectedImageIndex((curr) => curr - 1);
    }

    if (newIndex === selectedImageIndex && originalIndex > newIndex) {
      setSelectedImageIndex((curr) => curr + 1);
    }
  };

  const handleGalleryImageRemove = (indexToDelete) => {
    setSelectedImageIndex(undefined);

    const _images = images.filter((_, index) => index !== indexToDelete);

    if (_images.length > 0 && images.length === indexToDelete + 1) {
      replaceImage(indexToDelete - 1);
      cropRef.current.cropper.replace(_images[indexToDelete - 1].dataUrl);
    }

    if (_images.length > 0 && indexToDelete === 0) {
      replaceImage(0);
      cropRef.current.cropper.replace(_images[0].dataUrl);
    }

    setImages(_images);
  };

  const replaceImage = (index) => {
    setIsCropperReady(false);
    setCrop(undefined);
    setSelectedImageIndex(index);
  };

  return (
    <div
      className="cropper"
      ref={imageCropperRef}
      data-crop-type={circular ? "round" : ""}
    >
      {images.length > 0 && (
        <>
          <Cropper
            name={name}
            ref={cropRef}
            initialAspectRatio={aspect}
            aspectRatio={aspect}
            guides={false}
            viewMode={1}
            checkOrientation={false}
            checkCrossOrigin={false}
            crossOrigin="anonymous"
            modal={true}
            movable={true}
            zoomOnWheel={false}
            background={false}
            autoCrop={true}
            autoCropArea={1}
            style={style}
            ready={() => {
              setIsCropperReady(true);
            }}
            cropend={onCropEnd}
          />
          <TrashSvg
            className="cropper-image-picker-action"
            onClick={() => handleGalleryImageRemove(selectedImageIndex)}
          />
        </>
      )}
      {images.length === 0 && (
        <div
          className="cropper-image-picker"
          style={{ height: `${cropperHeight}px` ?? "100%" }}
        >
          <ImageUploadBox
            name={name}
            multiple={multiple}
            onChange={handleFileChange}
            height={cropperHeight}
          />
        </div>
      )}
      {withGallery === true && images.length > 0 && (
        <DragDropContext onDragEnd={handleGalleryImageDragEnd}>
          <div className="cropper__gallery-scroller">
            <Droppable
              key="droppableImages"
              droppableId="images"
              direction="horizontal"
            >
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  className="cropper__gallery"
                  {...provided.droppableProps}
                >
                  <label
                    className="cropper__gallery__uploader"
                    htmlFor="cropper__gallery__uploader__input"
                    style={{
                      textAlign: "center",
                      aspectRatio: aspectRatio ?? 1,
                    }}
                  >
                    <FileUploaderPlaceholderSvg height={36} />
                    Click to add more images
                    <input
                      id="cropper__gallery__uploader__input"
                      type="file"
                      multiple
                      accept="image/*"
                      onClick={(e) => {
                        e.target.value = null;
                      }}
                      style={{ display: "none" }}
                      onChange={(e) => handleFileChange(e.target.files)}
                    />
                  </label>
                  {[...images].map((image, index) => {
                    const className = ["cropper__gallery__image"];

                    if (selectedImageIndex === index) {
                      className.push("cropper__gallery__image--selected");
                    }

                    return (
                      <Draggable draggableId={index.toString()} index={index}>
                        {(provided) => (
                          <div
                            key={image.file.name}
                            className={className.join(" ")}
                            ref={provided.innerRef}
                            {...provided.dragHandleProps}
                            {...provided.draggableProps}
                            onClick={() => {
                              replaceImage(index);
                            }}
                          >
                            <img
                              className="cropper__gallery__image__preview"
                              alt="preview_image"
                              src={image.croppedDataUrl ?? image.dataUrl}
                              style={{
                                width: 130 * (aspectRatio ?? 1),
                              }}
                            />
                            <TrashSvg
                              className="cropper__gallery__image__trash"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGalleryImageRemove(index);
                              }}
                            />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
      )}
    </div>
  );
};

export default ImageCropperEx;
